import { addBreadcrumb, captureException } from '@sentry/browser';

import { getCountryAndLanguage } from './localization';
import { loadTokenService } from './tokenService';

const getWindowAuthToken = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!window.ikea?.authentication) {
      return reject(new Error('window.ikea.authentication is missing'));
    }

    window.ikea.authentication.getTokens((error, tokens) => {
      if (error || !tokens) {
        captureException(error ?? new Error('No tokens returned from token-service'));
        return reject(error ?? new Error('No tokens returned from token-service'));
      }

      if (tokens.auth) {
        return resolve(tokens.auth.value);
      } else if (tokens.guest) {
        return resolve(tokens.guest.value);
      }

      return reject(new Error('Could not get either guest or auth token from token-service'));
    });
  });
};

export const getHttpGuestToken = async (countryCode: string): Promise<string> => {
  const url = `${window.payorder.AUTH_BASE_URL}/v2/token`;
  const entraId = window.payorder.ENTRA_ID;

  if (typeof entraId !== 'string') {
    console.warn('[getHttpGuestToken] ENTRA_ID is not configured');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Id': entraId as string,
    },
    body: JSON.stringify({
      retailUnit: countryCode,
    }),
  });

  if (!response.ok) {
    const error = new Error('Guest token could not be retrieved from API');
    captureException(error);
    throw error;
  }

  const data = await response.json();
  const token = data.access_token;

  if (!token) {
    const error = new Error('Guest token is missing from API response body');
    captureException(error);
    throw error;
  }

  addBreadcrumb({
    category: 'auth',
    message: 'AUTH:HTTP_GUEST_TOKEN:RECEIVED',
    level: 'info',
  });

  return token;
};

// Run global script that fetches token and stores it as cookie.
export async function fetchAuthToken(): Promise<string> {
  const { countryCode, languageCode } = getCountryAndLanguage();

  if (process.env.NODE_ENV === 'staging') {
    return getHttpGuestToken(countryCode);
  }

  if (['development', 'test'].includes(process.env.NODE_ENV) && !window.ikea?.authentication) {
    await loadTokenService(countryCode.toLowerCase(), languageCode.toLowerCase());
  }

  return getWindowAuthToken();
}
